(ns manage.adset-decision-tree-page.view.table-util
  (:require ["@mui/material/Alert$default" :as Alert]
            ["@mui/material/CircularProgress$default" :as CircularProgress]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Tooltip$default" :as Tooltip]
            ["src/svg/path/tree-vertical.svg$default" :as TreeVerticalIcon]
            [cljs-bean.core :refer [->clj ->js]]
            [clojure.string :as str]
            [common.util :as util]
            [manage.ad-variant.page.columns :as columns]
            [manage.ad-variant.widgets :as variant-widgets]
            [manage.adset-decision-tree-page.navigation :as navigation]
            [manage.adset-decision-tree-page.view.common :refer [chip preview-button]]
            [manage.adset-decision-tree-page.view.context :as context]
            [uix.core :as uix :refer [$ defui]]))

(defn navigate-to-node [{:keys [label-path decision-tree-node-immutable-id] :as _row}]
  (let [[path-last path-llast] (reverse label-path)]
    (navigation/change-selection
     {:view-mode "tree"
      :immutable-node-id decision-tree-node-immutable-id
      :default-outcome (and (= path-last "Default")
                            (not= path-llast "Starting Point"))})))

(defn- render-activation-status-cell [^js params]
  (let [value (.-value params)]
    ($ Tooltip {:title (when (= "draft" value)
                         "Ad will only start being served after approval")}
       ($ :span
          ($ chip {:label value
                   :color (if (#{"draft" "partially draft" "all draft"} value)
                            "warning"
                            "")})))))

(def combined-status-labels
  "Chip labels for the combined 6-value activation status (Meta/TikTok only).
   Used by `render-combined-status-cell`; not applied to the base/DV grid, which
   keeps rendering its legacy `status` values verbatim."
  {"removed" "Removed"
   "pending" "Pending/Processing"
   "active-serving" "Active - Serving"
   "active-not-serving" "Active - Not serving"
   "inactive-scheduled" "Inactive - scheduled"
   "inactive-manual" "Inactive - manual"})

(defn render-combined-status-cell
  "Cell renderer for the combined activation status. Maps the raw value to a
   friendly label (falling back to the raw value, e.g. for the Meta `draft`
   overlay) and preserves the existing draft tooltip/warning colouring."
  [^js params]
  (let [value (.-value params)]
    ($ Tooltip {:title (when (= "draft" value)
                         "Ad will only start being served after approval")}
       ($ :span
          ($ chip {:label (get combined-status-labels value value)
                   :color (if (#{"draft" "partially draft" "all draft"} value)
                            "warning"
                            "")})))))

(def activation-status-column
  {:field "activationStatus"
   :headerName "Activation Status"
   :width 200
   :valueGetter (fn [_ row]
                  (if (= "draft" (:approval row))
                    "draft"
                    (:status row)))
   :renderCell render-activation-status-cell})

(def targeting-preview-column
  {:field "preview"
   :headerName "Preview"
   :valueGetter (fn [_value row] #{(:ad-variant-id row)})
   :renderCell (fn [^js params] ($ preview-button {:ids (.-value params)}))
   ;; Hide the name on header row
   :renderHeader (fn [^js _params])
   :cellClassName "hover-action-cell"
   :sortable false
   :filterable false
   :resizeable false
   ;; SvgIcon is 40px, 10px cell paddings
   :width 60})

(def navigate-to-node-column
  {:field "navigateToNode"
   :headerName "Navigate to node"
   :valueGetter (fn [_value row] row)
   :renderCell (fn [^js params]
                 ($ IconButton
                    {:onClick (fn [e]
                                (.stopPropagation e)
                                (navigate-to-node (.-value params)))
                     :sx #js {:color "grey.10"}}
                    ($ TreeVerticalIcon)))
   ;; Hide the name on header row
   :renderHeader (fn [^js _params])
   :cellClassName "hover-action-cell"
   :sortable false
   :filterable false
   :resizeable false
   ;; SvgIcon is 40px, 10px cell paddings
   :width 60})

(defn get-decision-path [row]
  (->> (get-in row [:label-path])
       (remove str/blank?)
       (str/join " / ")))

(def decision-path-column
  {:field "decisionPath"
   :sortable false
   :flex 1
   :headerName "Decision Path"
   :minWidth 300
   :type "string"
   :valueGetter (fn [_ row] (get-decision-path row))})

(def impressions-column
  {:field "impressions"
   :type "number"
   :headerName "Impressions"
   :valueGetter (fn [_ row]
                  (or (get-in row [:analytics :total :impressions])
                      0))
   :renderCell (fn [^js params]
                 (util/->locale-string (.-value params)))
   :sortable false})

(def clicks-column
  {:field "clicks"
   :type "number"
   :headerName "Clicks"
   :valueGetter (fn [_ row]
                  (or (get-in row [:analytics :total :clicks])
                      0))
   :renderCell (fn [^js params]
                 (util/->locale-string (.-value params)))
   :sortable false})

(def ctr-column
  {:field "ctr"
   :type "number"
   :headerName "CTR"
   :valueGetter (fn [_ row]
                  (-> (get-in row [:analytics :total])
                      ((juxt :impressions :clicks))))
   :renderCell (fn [^js params]
                 (let [[impressions clicks] (.-value params)
                       ctr (if (or (nil? impressions) (zero? impressions))
                             0
                             (/ clicks impressions))]
                   (str (util/->locale-string (* 100 ctr) {:minimumFractionDigits 3}) "%")))
   :sortable false})

(def grey-out-row-without-status-sx
  {(str ".greyed-out > .MuiDataGrid-cell:not([data-field='"
        (:field activation-status-column)
        "'], .hover-action-cell)")
   #js {:opacity 0.3}})

(def modal-table-default-sx
  {:maxWidth "100%"
   :width 900

   ;; Needs to be at least 220 to fit the loading spinner in the table
   ;; without scrollbars to fix another horizontally expanding table glitch
   :minHeight 220})

(def hidden-aggregation-column-header-label-sx
  {".MuiDataGrid-aggregationColumnHeaderLabel" #js {:display "none"}})

(def integration-error-filter
  {:label "Integration errors"
   :field "integration-error"
   :id "integration-error"
   :operator "isNotEmpty"
   :filter-options [{:value "true"
                     :label "Has integration errors"}]})

(def status-filter
  {:label "Status"
   :field (:field activation-status-column)
   :id "status"
   :data-testid "statusFilterSelect"
   :filter-options [{:value "pending"
                     :label "Pending"}
                    {:value "active"
                     :label "Active"}
                    {:value "paused"
                     :label "Paused"}
                    {:value "removed"
                     :label "Removed"}]})

(def combined-status-filter
  "Status filter for the Meta/TikTok grids, backed by the combined
   `activation-status` column (6 values). The base/DV grid keeps `status-filter`."
  {:label "Status"
   :field (:field activation-status-column)
   :id "status"
   :data-testid "statusFilterSelect"
   :filter-options (mapv (fn [value] {:value value
                                      :label (get combined-status-labels value)})
                         ["removed"
                          "pending"
                          "active-serving"
                          "active-not-serving"
                          "inactive-scheduled"
                          "inactive-manual"])})

(defui activation-procession-loading-alert []
  (let [{:keys [activation-processing]} (uix/use-context context/decision-trees-context)]
    (when activation-processing
      ($ Alert {:severity "info"
                :icon ($ CircularProgress {:size 24})
                :sx #js {:mb 3}}
         "Your Ad variants are being loaded. This can take a minute."))))

(defn get-ad-id [channel row]
  (case channel
    "meta" (or (:published-facebook-ad-id row)
               (get-in row [:sync-data :published-facebook-ad-id]))
    "tiktok" (get-in row [:data :published-tiktok-ad-id])
    nil))

(defn get-ad-group-id [channel row]
  (case channel
    "meta" (or (get-in row [:sync-data :facebook-adset-id])
               (get-in row [:data :facebook-adset-id]))
    "tiktok" (or (get-in row [:data :sync-data :ad-group-id])
                 (get-in row [:data :data :ad-group-id]))
    nil))

(defui identifier-with-schedule-column-cell [{{:keys [children childrenExpanded]} :rowNode
                                              {:keys [scheduling]} :row
                                              {:keys [getRow]} :api
                                              :as props}]
  (let [is-parent? (seq children)
        any-child-has-schedule (when is-parent?
                                 (some (fn [id] (-> id getRow :scheduling :timezone))
                                       children))
        has-schedule? (or (:timezone scheduling) any-child-has-schedule)]
    ($ Stack {:gap 1 :direction "row"}
       (when (and has-schedule? (not (and is-parent? childrenExpanded)))
         ($ variant-widgets/scheduling-info-icon scheduling))
       ($ columns/identifier-column-cell props))))

(def identifier-with-schedule-column
  (-> columns/identifier-column
      js->clj
      (assoc :renderCell (fn [^js params]
                           ($ identifier-with-schedule-column-cell (->clj params))))
      ->js))

(defn get-activation-identifier-column [has-grouping group-key]
  (if has-grouping
    (util/merge-objects identifier-with-schedule-column
                        #js {:groupingValueGetter (fn [_ row]
                                                    [(:content-id row)
                                                     (get row group-key)])
                             :renderCell (fn [^js params]
                                           ($ identifier-with-schedule-column-cell
                                              (assoc (->clj params) :value
                                                     (first (.-value params)))))})
    identifier-with-schedule-column))
